import React from 'react';

import moment from 'moment';

import { FlightLineBottomTextStrong } from 'components/Cards/DataCardGroupedFlights/FlightDetailCard/styles';
import { getTurnDays } from 'components/Cards/DataCardGroupedFlights/utils';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Sector } from 'models/sector';
import { getAirlineForInitials, getFlightClass, htmlT } from 'utils/generic';

import FlightSectorBaggage from './components/Baggage';
import {
  Container,
  FlightCard,
  AirlineLogo,
  AirlineName,
  FlightNumber,
  Info,
  FlightSection,
  FlightColumn,
  FlightTitle,
  FlightDate,
  FlightTime,
  FlightAirportInitials,
  FlightAirportName,
  FlightAirportCity,
  FlightLineContainer,
  FlightLine,
  FlightLineTopText,
  FlightLineBottomText,
  FlightTimeTurnDays,
  OperatorName,
} from './styles';

interface Props {
  sector: Sector;
  className?: string;
}

const getDurationInSeconds = (time?: string) => {
  if (!time) return 0;

  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60;
};

const FlightSector = ({ sector, className }: Props) => {
  const { t } = useStoreTranslation();

  const renderFlights = () =>
    sector.flights.map((flight, index) => {
      const nextFlight = sector.flights[index + 1];

      const turnDays = getTurnDays(flight.departureDate, flight.arrivalDate);

      let stopDuration;
      if (nextFlight) {
        stopDuration = moment
          .duration(moment(nextFlight.departureDate).utc().diff(moment(flight.arrivalDate).utc(), 'seconds'), 'seconds')
          .format('d[d] H[h] m[m]');
      }

      const duration = moment.duration(getDurationInSeconds(flight.flightTimeInternal), 'seconds').format(
        function () {
          if ((this as any).duration.asSeconds() >= 24 * 60 * 60) return 'd[d] h[h]';
          return 'h[h] m[m]';
        },
        { trim: 'all' },
      );

      return (
        <FlightSection key={flight.number}>
          <FlightCard>
            <FlightCard.Header>
              <AirlineLogo airline={flight.airlineCode} />
              <AirlineName>{getAirlineForInitials(flight.airlineCode)?.split(' ').first()}</AirlineName>
              <FlightNumber>
                {htmlT(t, 'flightSector.flightNumber', { airline: flight.airlineCode, number: flight.number })}
              </FlightNumber>
            </FlightCard.Header>
            {flight.opAirLineCode && flight.opAirLineName && flight.opAirLineCode !== flight.airlineCode &&<OperatorName>{t('flightsCard.detailCard.operatorAirline', { airlineName: `${flight.opAirLineName}` })}</OperatorName>}
            <FlightCard.Body>
              <FlightColumn>
                <FlightTitle>{t('flightSector.departure')}</FlightTitle>
                <FlightDate>{moment(flight.departureDate).utc().format('ddd[,] DD MMM')}</FlightDate>
                <FlightTime>{moment(flight.departureDate).utc().format('HH:mm')}</FlightTime>

                <FlightAirportInitials>{flight.departureAirport}</FlightAirportInitials>
                <FlightAirportCity>{flight.departureCityName}</FlightAirportCity>
                <FlightAirportName>{flight.departureAirportName}</FlightAirportName>
              </FlightColumn>

              <FlightColumn>
                <FlightLineContainer>
                  <FlightLineTopText>{duration}</FlightLineTopText>
                  <FlightLine />
                  <FlightLineBottomTextStrong>{t(getFlightClass(flight.cabin))}</FlightLineBottomTextStrong>
                  <FlightLineBottomText>{flight.equipment}</FlightLineBottomText>
                  {flight.fareFamilies && (
                    <FlightLineBottomText>
                      {t('flightSector.fare', { fare: flight.fareFamilies.capitalize() })}
                    </FlightLineBottomText>
                  )}
                </FlightLineContainer>
              </FlightColumn>

              <FlightColumn right>
                <FlightTitle>{t('flightSector.arrival')}</FlightTitle>
                <FlightDate>{moment(flight.arrivalDate).utc().format('ddd[,] DD MMM')}</FlightDate>
                <FlightTime>
                  <>
                    {moment(flight.arrivalDate).utc().format('HH:mm')}
                    {turnDays !== 0 && (
                      <FlightTimeTurnDays>{`${turnDays > 0 ? '+' : '-'} ${turnDays}`}</FlightTimeTurnDays>
                    )}
                  </>
                </FlightTime>

                <FlightAirportInitials>{flight.arrivalAirport}</FlightAirportInitials>
                <FlightAirportCity>{flight.arrivalCityName}</FlightAirportCity>
                <FlightAirportName>{flight.arrivalAirportName}</FlightAirportName>
              </FlightColumn>

              <FlightSectorBaggage restrictions={flight.baggageRestrictions} handBaggage={flight.handBaggage} />
            </FlightCard.Body>
          </FlightCard>
          {nextFlight && (
            <Info>
              <Info.Time src="/corporate/images/time-info-active.svg" />
              <Info.Message>
                {htmlT(t, 'flightSector.flightStop', {
                  duration: stopDuration,
                  city: nextFlight.departureCityName,
                })}
              </Info.Message>
            </Info>
          )}
        </FlightSection>
      );
    });

  return (
    <Container className={className || ''}>
      {renderFlights()}
      <Info gray>
        <Info.Time src="/corporate/images/time.svg" />
        <Info.Message gray>
          {t('flightSector.totalDuration', {
            duration: moment.duration(sector.totalDurationInSeconds, 'seconds').format('d[d] H[h] m[m]'),
          })}
        </Info.Message>
      </Info>
    </Container>
  );
};

export default FlightSector;
